import React from 'react';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  BarElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend,
  Title,
} from 'chart.js';

ChartJS.register(BarElement, CategoryScale, LinearScale, Tooltip, Legend, Title);

export const YearlyChart = ({ datasets }) => {
  // Define color palette
  const colors = [
    '#4facfe', '#00f2fe', '#43e97b', '#f441a5', '#ffce00',
    '#ff5f6d', '#845ec2', '#ffc75f', '#f9f871', '#00c9a7',
    '#f67280', '#4bc0c0'
  ];

  // Build: { schemeName: { yearOrFinYear: count } }
  const schemeYearMap = {};

  datasets.forEach(dataset => {
    dataset?.responseArray?.forEach(item => {
      const scheme = item?.gisSchemeName || 'Unknown Scheme';
      const dateStr =
        item?.createdDate ||
        item?.createdOn ||
        item?.financialYear ||
        item?.project_Geo_Update_Date ||
        item?.registraionDate ||
        item?.finYear;

      if (!dateStr) return;

      let yearKey;

      // Case 1: Financial year format like "2023-24"
      if (/^\d{4}-\d{2}$/.test(dateStr)) {
        yearKey = dateStr;
      } else {
        // Case 2: Normal date string
        const parsedDate = new Date(dateStr);
        if (!isNaN(parsedDate)) {
          yearKey = parsedDate.getFullYear().toString();
        }
      }

      if (!yearKey) return;

      if (!schemeYearMap[scheme]) schemeYearMap[scheme] = {};
      schemeYearMap[scheme][yearKey] = (schemeYearMap[scheme][yearKey] || 0) + 1;
    });
  });

  // Extract sorted list of years/financial years
  const allYears = Array.from(
    new Set(Object.values(schemeYearMap).flatMap(obj => Object.keys(obj)))
  ).sort((a, b) => {
    // If both are financial years like 2023-24
    if (/^\d{4}-\d{2}$/.test(a) && /^\d{4}-\d{2}$/.test(b)) {
      return a.localeCompare(b);
    }
    // If both are normal numeric years
    if (/^\d{4}$/.test(a) && /^\d{4}$/.test(b)) {
      return parseInt(a) - parseInt(b);
    }
    // Otherwise keep financial years after numeric years
    if (/^\d{4}$/.test(a)) return -1;
    if (/^\d{4}$/.test(b)) return 1;
    return a.localeCompare(b);
  });

  // Prepare datasets for chart
  const chartDatasets = Object.entries(schemeYearMap).map(([scheme, yearData], index) => ({
    label: scheme,
    data: allYears.map(year => yearData[year] || 0),
    backgroundColor: colors[index % colors.length],
    borderColor: '#fff',
    borderWidth: 1,
    borderRadius: 6,
    barPercentage: 0.6
  }));

  // Total surveys
  const totalSurveys = chartDatasets
    .flatMap(ds => ds.data)
    .reduce((sum, val) => sum + val, 0);

  const chartData = {
    labels: allYears,
    datasets: chartDatasets,
  };

  const options = {
    responsive: true,
    plugins: {
      title: {
        display: true,
        text: 'Survey Count by Financial Year',
        align: 'start',
        font: { size: 25, weight: 'bold', family: 'Beiruti, sans-serif' },
        color: '#222',
        padding: { top: 10, bottom: 20 }
      },
      legend: {
        display: true,
        position: 'bottom',
        labels: {
          font: { size: 14, weight: 'bold', family: 'Beiruti, sans-serif' },
          color: '#333'
        }
      },
      tooltip: {
        callbacks: {
          label: (context) =>
            `${context.dataset.label}: ${context.parsed.y.toLocaleString()} surveys`
        }
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          font: { size: 13, weight: 600, family: 'Beiruti, sans-serif' },
          color: '#000',
          callback: value => value.toLocaleString()
        },
        grid: { color: 'rgba(0,0,0,0.05)' }
      },
      x: {
        ticks: {
          font: { size: 13, weight: 600, family: 'Beiruti, sans-serif' },
          color: '#000'
        },
        grid: { color: 'rgba(0,0,0,0.03)' }
      }
    },
    animation: {
      duration: 1200,
      easing: 'easeOutBounce'
    }
  };

  return (
    <>
      <div className='row mt-5'>
        <div className='col-md-8 leftbar'>
          <div style={{ height: '300px' }}>
            <Bar data={chartData} options={options} />
          </div>
        </div>
        <div className='col-md-4'>
          <div
            style={{
              maxHeight: '320px',
              height: '320px',
              background: '#fff',
              border: '1px solid #ddd',
              borderRadius: '8px',
              boxShadow: '0 4px 8px rgba(0,0,0,0.05)',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden'
            }}
          >
            <div style={{ padding: '10px 20px', background: '#f9f9f9', borderBottom: '1px solid #eee' }}>
              <h5 style={{ fontWeight: 700, marginBottom: '8px', color: '#000' }}>
                📊 Year-Wise Survey Summary
              </h5>
            </div>

            {/* Scrollable area */}
            <div style={{ overflowY: 'auto', flex: 1, padding: '10px 20px' }}>
              {Object.entries(schemeYearMap).map(([scheme, yearData], schemeIdx) => (
                <div key={scheme} style={{ marginBottom: '15px' }}>
                  <div style={{ fontWeight: 600, marginBottom: '5px', fontSize: '18px', color: '#555' }}>
                    🔹 {scheme}
                  </div>
                  {allYears.map((year, i) => (
                    <div
                      key={`${scheme}-${year}`}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        fontSize: '14px',
                        marginLeft: '6px',
                        marginBottom: '6px'
                      }}
                    >
                      <div
                        style={{
                          width: '8px',
                          height: '8px',
                          backgroundColor: colors[schemeIdx % colors.length],
                          borderRadius: '50%',
                          marginRight: '8px'
                        }}
                      />
                      <span style={{ fontWeight: 500 }}>{year}</span>
                      <span style={{ marginLeft: 'auto' }}>
                        {(yearData[year] || 0).toLocaleString()} surveys
                      </span>
                    </div>
                  ))}
                </div>
              ))}
            </div>

            {/* Sticky Total */}
            <div
              style={{
                padding: '10px 20px',
                background: '#fff',
                borderTop: '1px solid #ddd',
                fontWeight: 600,
                fontSize: '20px',
                textAlign: 'right',
                color: '#000',
                bottom: 0,
                zIndex: 1
              }}
            >
              Total: {totalSurveys.toLocaleString()} surveys
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
